import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/layout/Logo";
import {
  Mail,
  ArrowRight,
  ArrowLeft,
  KeyRound,
  RefreshCw,
  Sparkles,
  Loader2,
} from "lucide-react";
import {
  PORTAL_LOGIN_STEP_EMAIL,
  PORTAL_LOGIN_STEP_CONFIRM_REG,
  PORTAL_LOGIN_STEP_OTP,
  isValidPortalEmail,
  isValidPortalOtp,
  decidePortalLoginStep,
} from "@/lib/AuthEngine";

/**
 * Guest Portal — Passwordless OTP Login (Iter-358 + Iter-361 polish).
 *
 * Flow:
 *   1. Guest enters email → click Continue.
 *   2. Frontend calls POST /auth/customer/login-check-email.
 *      • exists=false → warm "We couldn't find that email" prompt with
 *        two clear paths: Create an account · Try a different email.
 *      • exists=true  → automatically request an OTP and switch to the
 *        6-slot code screen.
 *   3. Guest enters OTP → POST /auth/customer/login-verify-otp → JWT +
 *      user → landing on `/portal/appointments` (My Visit agenda).
 *
 * Iter-361 additions:
 *   • Premium editorial hero (left pane) with the Cocoon voice.
 *   • Refined empty-state for unknown emails (warm, inviting).
 *   • 6-slot `InputOTP` with auto-submit-ready styling.
 *   • Resend-OTP cooldown timer (60 s) — button disabled while the
 *     countdown ticks and shows "Send a new code in 45s".
 *
 * Engine boundary: consumes helpers from `AuthEngine.jsx` (IMMUTABLE).
 * Staff login (username + password) is UNCHANGED and lives on its own
 * page — this component is Guest-only.
 */

const RESEND_COOLDOWN_SEC = 60;

export default function CustomerLogin() {
  const {
    checkCustomerEmail,
    requestCustomerLoginOtp,
    verifyCustomerLoginOtp,
  } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState(PORTAL_LOGIN_STEP_EMAIL);
  const [email, setEmail] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const emailInputRef = useRef(null);
  const autoSubmittedRef = useRef("");

  const emailValid = useMemo(() => isValidPortalEmail(email), [email]);
  const otpValid = useMemo(() => isValidPortalOtp(otp), [otp]);
  const trimmedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  useEffect(() => {
    if (step === PORTAL_LOGIN_STEP_EMAIL && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [step]);

  // Resend cooldown ticker — decrements once per second while > 0.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => {
      setResendIn((n) => (n <= 1 ? 0 : n - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const sendOtp = async (targetEmail) => {
    const r = await requestCustomerLoginOtp(targetEmail);
    setPendingId(r.pending_id);
    setResendIn(RESEND_COOLDOWN_SEC);
    if (r.dev_otp) {
      toast.info(`Dev mode — code: ${r.dev_otp}`, { duration: 8000 });
    } else {
      toast.success("Sign-in code sent to your email");
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailValid) {
      toast.error("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const check = await checkCustomerEmail(trimmedEmail);
      const next = decidePortalLoginStep({ currentStep: step, checkEmailResult: check });
      if (next === PORTAL_LOGIN_STEP_OTP) {
        await sendOtp(check.email);
      }
      setStep(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRegisterYes = () => {
    nav(`/portal/register${trimmedEmail ? `?email=${encodeURIComponent(trimmedEmail)}` : ""}`);
  };

  const handleRegisterNo = () => {
    setEmail("");
    setPendingId("");
    setOtp("");
    setStep(PORTAL_LOGIN_STEP_EMAIL);
  };

  const handleOtpSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!otpValid) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setBusy(true);
    try {
      const { previousSessionReplaced } = await verifyCustomerLoginOtp(pendingId, otp.trim());
      toast.success("Welcome back to Cocoon");
      if (previousSessionReplaced) {
        toast.info("Signed out of your previous device.", { duration: 4500 });
      }
      nav("/portal/appointments");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Auto-submit once all 6 digits are entered (the Sign in button stays available).
  useEffect(() => {
    if (step !== PORTAL_LOGIN_STEP_OTP || !otpValid || busy || !pendingId) return;
    if (autoSubmittedRef.current === otp.trim()) return;
    autoSubmittedRef.current = otp.trim();
    handleOtpSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, otpValid, busy, pendingId, step]);

  const handleResend = async () => {
    if (!emailValid || resendIn > 0 || busy) return;
    setBusy(true);
    try {
      await sendOtp(trimmedEmail);
      setOtp("");
      autoSubmittedRef.current = "";
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleBackToEmail = () => {
    setStep(PORTAL_LOGIN_STEP_EMAIL);
    setOtp("");
    setPendingId("");
    setResendIn(0);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* --------------------------- HERO PANE ------------------------- */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          alt="Cocoon Medical Spa treatment room"
          className="absolute inset-0 w-full h-full object-cover"
          src="https://images.unsplash.com/photo-1693578538512-fc66f318c833?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGElMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODUxMjg2NTF8MA&ixlib=rb-4.1.0&q=85"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-foreground/25" />

        <div className="absolute top-10 left-10 right-10 flex items-center gap-3">
          <div className="tracking-overline text-[10px] text-white/85 drop-shadow-sm">
            COCOON · MEMBER PORTAL
          </div>
        </div>

        <div className="absolute bottom-10 left-10 right-10 backdrop-blur-xl bg-background/80 rounded-2xl p-8 border border-border shadow-lg">
          <p className="tracking-overline text-[10px] text-muted-foreground">
            YOUR VISITS, YOUR RITUAL
          </p>
          <p className="font-editorial text-4xl mt-2 leading-[1.05] text-foreground">
            Quiet, considered,
            <br />
            <span className="italic text-primary">yours</span>.
          </p>
          <p className="text-sm text-muted-foreground mt-4 max-w-md leading-relaxed">
            Sign in to review upcoming appointments, revisit past sessions, and stay connected with your care team — all in one calm place.
          </p>
        </div>
      </div>

      {/* --------------------------- FORM PANE ------------------------- */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="w-full max-w-sm" data-testid="customer-login-shell">
          <div className="flex items-center justify-between">
            <Logo variant="mark" to="/" size={56} />
            <span className="tracking-overline text-[10px] text-muted-foreground hidden sm:inline">
              GUEST SIGN-IN
            </span>
          </div>

          {/* ----------------------- Step 1 · Email --------------------- */}
          {step === PORTAL_LOGIN_STEP_EMAIL && (
            <form
              onSubmit={handleEmailSubmit}
              data-testid="customer-login-form-email"
              className="animate-in fade-in duration-500"
            >
              <h1 className="font-editorial text-[42px] leading-[1.05] mt-10">
                Welcome back
              </h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Enter your email and we'll send a one-time sign-in code. No passwords to remember — just you and a quiet moment.
              </p>

              <div className="mt-9 space-y-5">
                <div>
                  <Label htmlFor="email" className="text-xs tracking-wider uppercase text-foreground/80">
                    Email address
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      id="email"
                      ref={emailInputRef}
                      data-testid="customer-login-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-9 h-11 rounded-lg"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 rounded-full text-sm font-medium tracking-wide"
                  disabled={busy || !emailValid}
                  data-testid="customer-login-continue"
                >
                  {busy ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending your code…
                    </>
                  ) : (
                    <>
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-muted-foreground text-center pt-2">
                  New to Cocoon?{" "}
                  <button
                    type="button"
                    onClick={() => nav("/portal/register")}
                    className="text-foreground underline underline-offset-4 decoration-primary/50 hover:decoration-primary transition"
                    data-testid="customer-register-link"
                  >
                    Create your account
                  </button>
                </p>
              </div>

              <p className="mt-10 text-[11px] leading-relaxed text-muted-foreground/80 text-center">
                Are you a Cocoon team member?{" "}
                <button
                  type="button"
                  onClick={() => nav("/staff/login")}
                  className="underline underline-offset-2 hover:text-foreground"
                  data-testid="staff-login-hint"
                >
                  Sign in here
                </button>
              </p>
            </form>
          )}

          {/* ----------------- Step 2 · Register prompt ----------------- */}
          {step === PORTAL_LOGIN_STEP_CONFIRM_REG && (
            <div
              data-testid="customer-login-not-registered"
              className="animate-in fade-in slide-in-from-right-2 duration-500"
            >
              <div className="mt-10 inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 border border-primary/25">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>

              <h1 className="font-editorial text-[38px] leading-[1.05] mt-6">
                Let's begin your journey
              </h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                We couldn't find a Cocoon account for{" "}
                <span className="font-medium text-foreground">{trimmedEmail || email}</span>.
                It only takes a moment to join — and once you do, this same email will sign you in every time.
              </p>

              <div className="mt-9 space-y-3">
                <Button
                  type="button"
                  className="w-full h-11 rounded-full text-sm font-medium tracking-wide"
                  onClick={handleRegisterYes}
                  data-testid="customer-login-register-yes"
                >
                  Create your Cocoon account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-11 rounded-full text-sm text-muted-foreground hover:text-foreground"
                  onClick={handleRegisterNo}
                  data-testid="customer-login-register-no"
                >
                  Try a different email
                </Button>
              </div>

              <p className="mt-8 text-[11px] leading-relaxed text-muted-foreground/80 text-center">
                Registration is quick — name, contact and you're in. No passwords required.
              </p>
            </div>
          )}

          {/* --------------------- Step 3 · OTP ------------------------- */}
          {step === PORTAL_LOGIN_STEP_OTP && (
            <form
              onSubmit={handleOtpSubmit}
              data-testid="customer-login-form-otp"
              className="animate-in fade-in slide-in-from-right-2 duration-500"
            >
              <div className="mt-10 inline-flex items-center gap-1 text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="inline-flex items-center hover:text-foreground transition"
                  data-testid="customer-login-back-to-email"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                  Change email
                </button>
              </div>

              <h1 className="font-editorial text-[42px] leading-[1.05] mt-4">
                Check your inbox
              </h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                We sent a 6-digit sign-in code to{" "}
                <span className="font-medium text-foreground">{trimmedEmail || email}</span>. It's valid for 15 minutes.
              </p>

              <div className="mt-8 space-y-6">
                <div>
                  <Label className="text-xs tracking-wider uppercase text-foreground/80">
                    Sign-in code
                  </Label>
                  <div className="mt-3 flex justify-center" data-testid="customer-login-otp-wrapper">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={(v) => setOtp(v.replace(/\D/g, ""))}
                      autoFocus
                      containerClassName="gap-2"
                      data-testid="customer-login-otp"
                    >
                      <InputOTPGroup className="gap-2">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot
                            key={i}
                            index={i}
                            className="h-12 w-11 text-lg font-medium rounded-md border first:rounded-md last:rounded-md"
                            data-testid={`customer-login-otp-slot-${i}`}
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 rounded-full text-sm font-medium tracking-wide"
                  disabled={busy || !otpValid}
                  data-testid="customer-login-verify"
                >
                  {busy ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing you in…
                    </>
                  ) : (
                    <>
                      <KeyRound className="w-4 h-4 mr-2" />
                      Sign in
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleResend}
                  disabled={busy || resendIn > 0}
                  className="w-full text-xs text-muted-foreground hover:text-foreground disabled:hover:text-muted-foreground disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 transition"
                  data-testid="customer-login-resend"
                >
                  <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
                  {resendIn > 0 ? (
                    <span data-testid="customer-login-resend-countdown">
                      Send a new code in {resendIn}s
                    </span>
                  ) : (
                    <span>Didn't receive it? Send a new code</span>
                  )}
                </button>
              </div>

              <p className="mt-8 text-[11px] leading-relaxed text-muted-foreground/80 text-center">
                Check your spam folder if the email doesn't arrive within a minute.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
